import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../../components/AuthLayout';
import { Label } from '@werkstatt/ui';
import { Alert, AlertDescription } from '@werkstatt/ui';
import { CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Passwort muss mindestens einen Großbuchstaben enthalten');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Passwort muss mindestens einen Kleinbuchstaben enthalten');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Passwort muss mindestens eine Ziffer enthalten');
      return;
    }
    if (!token) {
      setError('Ungültiger Reset-Link. Bitte fordern Sie einen neuen Link an.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: newPassword });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
      setError(detail || 'Zurücksetzen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Neues Passwort festlegen"
      subtitle="Geben Sie Ihr neues Passwort ein. Mindestens 8 Zeichen, Groß-/Kleinbuchstaben und Ziffer."
      footer={<p>Werkstatt-Connect © 2026</p>}
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-300">
              Passwort wurde erfolgreich zurückgesetzt! Sie werden zur Anmeldeseite weitergeleitet…
            </AlertDescription>
          </Alert>
        )}

        {!success && (
          <>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newPassword">Neues Passwort</Label>
                <input
                  id="newPassword"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1.5 block w-full px-3 py-2 border border-border rounded-lg shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="Mindestens 8 Zeichen"
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1.5 block w-full px-3 py-2 border border-border rounded-lg shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="Passwort wiederholen"
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Wird zurückgesetzt…' : 'Passwort zurücksetzen'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2.5 px-4 border border-border rounded-lg text-sm font-medium text-foreground bg-background hover:bg-muted focus:outline-none transition-colors"
              >
                Zurück zur Anmeldung
              </button>
            </div>
          </>
        )}
      </form>
    </AuthLayout>
  );
}
